'use client';

import * as React from 'react';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { cn } from '@/hen-ui/utils';
import { Button } from '@/hen-ui/shadcn/button';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
  autoPlay?: boolean;
  autoPlayDelay?: number;
  /**
   * Wrap from the last slide back to the first. When false, Previous is
   * unavailable on the first slide, Next on the last, and autoplay stops at the
   * end. Takes precedence over `opts.loop`; defaults to `opts.loop`, then true.
   */
  loop?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  scrollTo: (index: number) => void;
  selectedIndex: number;
  scrollSnaps: number[];
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      autoPlay = true,
      autoPlayDelay = 5000,
      loop: loopProp,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const loop = loopProp ?? opts?.loop ?? true;
    const autoplay = React.useMemo(
      () =>
        Autoplay({
          delay: autoPlayDelay,
          stopOnInteraction: true,
          stopOnLastSnap: !loop,
        }),
      [autoPlayDelay, loop],
    );

    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
        loop,
      },
      autoPlay ? [autoplay, ...(plugins ?? [])] : [...(plugins ?? [])],
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

    const refresh = React.useCallback((emblaApi: CarouselApi) => {
      if (!emblaApi) {
        return;
      }

      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setScrollSnaps(emblaApi.scrollSnapList());
    }, []);

    const stopAutoplay = React.useCallback(() => {
      api?.plugins().autoplay?.stop();
    }, [api]);

    const scrollPrev = React.useCallback(() => {
      if (!api?.canScrollPrev()) {
        return;
      }
      api.scrollPrev();
      stopAutoplay();
    }, [api, stopAutoplay]);

    const scrollNext = React.useCallback(() => {
      if (!api?.canScrollNext()) {
        return;
      }
      api.scrollNext();
      stopAutoplay();
    }, [api, stopAutoplay]);

    const scrollTo = React.useCallback(
      (index: number) => {
        api?.scrollTo(index);
        stopAutoplay();
      },
      [api, stopAutoplay],
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      refresh(api);
      api.on('reInit', refresh);
      api.on('select', refresh);

      return () => {
        api.off('reInit', refresh);
        api.off('select', refresh);
      };
    }, [api, refresh]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          scrollTo,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          scrollSnaps,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    /**
     * Extra classes for the Embla viewport (the `overflow-hidden` element).
     * Use a padding + negative-margin pair (e.g. `-my-2 py-2`) to keep slide
     * borders/shadows from being clipped by the viewport without shifting layout.
     */
    viewportClassName?: string;
  }
>(({ className, viewportClassName, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={cn('min-w-0 overflow-hidden', viewportClassName)}
    >
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(
  (
    {
      className,
      variant = 'outline',
      size = 'icon',
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        data-slot='carousel-previous'
        variant={variant}
        size={size}
        type={type}
        className={cn(
          'absolute h-6 w-6 rounded-full aria-disabled:pointer-events-none aria-disabled:opacity-50',
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        aria-disabled={!canScrollPrev || undefined}
        onClick={scrollPrev}
        {...props}
      >
        <ArrowLeft className='h-4 w-4' aria-hidden='true' />
        <span className='sr-only'>Previous slide</span>
      </Button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button>
>(
  (
    {
      className,
      variant = 'outline',
      size = 'icon',
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        data-slot='carousel-next'
        variant={variant}
        size={size}
        type={type}
        className={cn(
          'absolute h-6 w-6 rounded-full aria-disabled:pointer-events-none aria-disabled:opacity-50',
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        aria-disabled={!canScrollNext || undefined}
        onClick={scrollNext}
        {...props}
      >
        <ArrowRight className='h-4 w-4' aria-hidden='true' />
        <span className='sr-only'>Next slide</span>
      </Button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { scrollTo, selectedIndex, scrollSnaps } = useCarousel();

  return (
    <div
      ref={ref}
      aria-label='Slide navigation'
      className={cn('flex items-center justify-center gap-5', className)}
      {...props}
    >
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          type='button'
          aria-current={selectedIndex === index ? 'page' : undefined}
          className={cn(
            'h-2.5 w-2.5 rounded-full transition-all',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
            selectedIndex === index
              ? 'bg-neutral-900'
              : 'bg-neutral-100 hover:bg-neutral-200',
          )}
          onClick={() => scrollTo(index)}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
});
CarouselDots.displayName = 'CarouselDots';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
  useCarousel,
};
